// Geometry of the field-placement diagram, shared in spirit with the ticker overlay's
// `field-placement` component: a 100x100 square, boundary circle r=48 centred at (50, 50), the
// pitch in the middle, batter's end at the bottom. Positions are stored in these 0..100 units and
// sent to the backend as 0..1 fractions of the diagram.

export const FIELD_SIZE = 100;
export const FIELD_CENTER = 50;
export const FIELD_RADIUS = 48;
// A side fields eleven, so no more chips than this can be placed.
export const MAX_FIELDERS = 11;

export interface FieldPoint {
  x: number;
  y: number;
  // The one fielder the streamer called out, if any (at most one, enforced server-side).
  highlighted?: boolean;
}

// Whether a point (0..100 units) is on the field, i.e. inside the boundary circle.
export function isInsideField(point: FieldPoint): boolean {
  return Math.hypot(point.x - FIELD_CENTER, point.y - FIELD_CENTER) <= FIELD_RADIUS;
}

// Pulls a point that was dragged past the boundary back onto it, along the line to the centre.
export function clampToField(point: FieldPoint): FieldPoint {
  const dx = point.x - FIELD_CENTER;
  const dy = point.y - FIELD_CENTER;
  const distance = Math.hypot(dx, dy);
  if (distance <= FIELD_RADIUS) return point;
  const scale = FIELD_RADIUS / distance;
  return { x: FIELD_CENTER + dx * scale, y: FIELD_CENTER + dy * scale };
}

// Client (mouse / touch) coordinates -> diagram units, given the diagram's on-screen rectangle.
export function toFieldUnits(clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }): FieldPoint {
  return {
    x: ((clientX - rect.left) / rect.width) * FIELD_SIZE,
    y: ((clientY - rect.top) / rect.height) * FIELD_SIZE,
  };
}

// The request payload: 0..1 fractions rounded to 4 decimals, the same precision the backend keeps.
export function toFractions(points: FieldPoint[]): FieldPoint[] {
  const round = (value: number) => Math.round((value / FIELD_SIZE) * 10000) / 10000;
  return points.map((p) => ({ x: round(p.x), y: round(p.y), highlighted: p.highlighted }));
}

// Inverse of `toFractions`, to reopen the dialog on the last placement that was sent.
export function fromFractions(points: FieldPoint[]): FieldPoint[] {
  return points.map((p) => ({ x: p.x * FIELD_SIZE, y: p.y * FIELD_SIZE, highlighted: p.highlighted }));
}
